// Cost by state (PRD TX2): every counted hour, in the state the person worked in that day (TX1), on
// the project it was reported to, costed at the person's actual rate for the month (§3.11). Wages are
// pay only, without employer taxes or benefits, and only employees have them: partners take guaranteed
// payments and 1099s are paid fees, so theirs is cost alone. Unpaid time off (3SUTO) costs nothing and
// is left out.

import { monthOf, type Iso, type Month } from "./dates";
import { UNPAID_TIME_OFF_CODE, type PersonMonthCost } from "./actual-cost";
import type { PayType } from "./people";

/** How the report groups pay types: employees, partners and 1099s. */
export type WorkerGroup = "FTE" | "Partner" | "1099" | "No pay";
export const WORKER_GROUPS: readonly WorkerGroup[] = ["FTE", "Partner", "1099", "No pay"];

export const workerGroup = (t: PayType | null): WorkerGroup =>
  t === "exempt" || t === "hourly" ? "FTE" : t === "partner" ? "Partner" : t === "contractor-hourly" || t === "contractor-monthly" ? "1099" : "No pay";

/** No work state on the person's card for the day. */
export const NO_STATE = "unknown";

export interface StateHours {
  personId: string;
  day: Iso;
  /** "" when the hours have no project code yet. */
  code: string;
  state: string;
  hours: number;
}

export interface StateCostRow {
  code: string;
  group: WorkerGroup;
  personId: string;
  state: string;
  hours: number;
  wages: number;
  cost: number;
}

/**
 * One row per project × person × state. `monthCosts` is keyed `personId|month`; `wageShare` is the
 * part of a person's loaded cost that is pay, for the month (0 for anyone not on payroll).
 */
export function stateCostRows(
  hours: readonly StateHours[],
  monthCosts: ReadonlyMap<string, PersonMonthCost>,
  wageShare: (personId: string, month: Month) => number,
): StateCostRow[] {
  const rows = new Map<string, StateCostRow>();
  for (const h of hours) {
    if (!h.hours || h.code === UNPAID_TIME_OFF_CODE) continue;
    const month = monthOf(h.day);
    const mc = monthCosts.get(`${h.personId}|${month}`);
    const group = workerGroup(mc?.type ?? null);
    const cost = mc ? h.hours * mc.rate : 0;
    const wages = group === "FTE" ? cost * wageShare(h.personId, month) : 0;
    const key = `${h.code}|${h.personId}|${h.state}`;
    const row = rows.get(key) ?? { code: h.code, group, personId: h.personId, state: h.state, hours: 0, wages: 0, cost: 0 };
    row.hours += h.hours;
    row.wages += wages;
    row.cost += cost;
    // A person whose pay type changed mid-range takes the latest one seen.
    if (group !== "No pay") row.group = group;
    rows.set(key, row);
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return [...rows.values()]
    .map((r) => ({ ...r, hours: r2(r.hours), wages: r2(r.wages), cost: r2(r.cost) }))
    .sort((a, b) => a.code.localeCompare(b.code) || a.personId.localeCompare(b.personId) || a.state.localeCompare(b.state));
}

/** The rows as CSV for Excel: year, project, pay type, person, state, hours, wages, loaded cost, 1099 cost. */
export function stateCostCsv(rows: readonly StateCostRow[], note: string, names: (personId: string) => string, codeName: (code: string) => string): string {
  const cell = (v: unknown) => {
    const s = v === undefined || v === null ? "" : String(v);
    const safe = /^[=+\-@]/.test(s) && !/^-?\d/.test(s) ? `'${s}` : s;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const head = ["Project code", "Project", "Pay type", "Person", "State", "Hours", "Wages", "Loaded cost", "1099 cost"];
  const lines = rows.map((r) => [
    r.code || "No project code",
    r.code ? codeName(r.code) : "",
    r.group,
    names(r.personId),
    r.state === NO_STATE ? "No work state" : r.state,
    r.hours.toFixed(2),
    r.wages.toFixed(2),
    r.group === "1099" ? "" : r.cost.toFixed(2),
    r.group === "1099" ? r.cost.toFixed(2) : "",
  ]);
  return [note, "", head, ...lines].map((l) => (typeof l === "string" ? cell(l) : l.map(cell).join(","))).join("\r\n") + "\r\n";
}
